// src/components/FileUploadBox.jsx
import { useRef, useState } from "react";
import { Box, Typography, Stack, Snackbar, Alert, Slide } from "@mui/material";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import { useAlerts } from "../Hooks";
import { sharedSnackbarAlertSx } from "../HelperFunctions/HelperFunctions";

function TransitionLeft(props) {
  return <Slide {...props} direction="left" />;
}

const FileUploadBox = ({
  onFileSelect,
  disableDragAndDrop,
  sampleFile,
  sampleFileName = "Sample Template.xlsx",
  allowedExtensions = [".csv", ".xlsx", ".xls", ".pdf", ".txt"],
  email,
  onUploadClick,
  disabled = false,
  extraLeftContent,
  onSampleDownload,
}) => {

  const inputRef = useRef();
  const [isDragging, setIsDragging] = useState(false);
  const { snackbar, showSnackbar, hideSnackbar } = useAlerts();

  const normalizedAllowedExtensions = (allowedExtensions || [])
    .map((ext) => String(ext || "").trim().toLowerCase())
    .filter(Boolean)
    .map((ext) => (ext.startsWith(".") ? ext : `.${ext}`));

  const acceptedFormatsText = normalizedAllowedExtensions.join(", ");

  const processFile = (fileList) => {
    if (disabled) return;
    const uploadedFile = fileList[0];
    if (!uploadedFile) return;

    const fileName = uploadedFile.name || "";

    const hasSpecialChars = /[^a-zA-Z0-9.\-_ ()]/g.test(fileName);
    if (hasSpecialChars) {
      showSnackbar("File name cannot contain special characters.", 'error');
      if (inputRef.current) inputRef.current.value = "";
      return;
    }

    const dotIndex = fileName.lastIndexOf(".");
    const fileExt = dotIndex >= 0 ? fileName.slice(dotIndex).toLowerCase() : "";

    if (normalizedAllowedExtensions.length > 0 && !normalizedAllowedExtensions.includes(fileExt)) {
      showSnackbar(`Invalid file format. Allowed: ${acceptedFormatsText}`, 'error');
      if (inputRef.current) inputRef.current.value = "";
      return;
    }

    if (uploadedFile.size > 5 * 1024 * 1024) {
      showSnackbar("File size exceeds 5MB limit.", 'error');
      if (inputRef.current) inputRef.current.value = "";
      return;
    }

    if (onFileSelect) {
      onFileSelect(uploadedFile);
    }
  };

  const handleDownload = (e) => {
    e.stopPropagation();
    if (onSampleDownload) {
      onSampleDownload(e);
      return;
    }
    if (sampleFile) {
      const link = document.createElement("a");
      link.href = sampleFile;
      link.setAttribute("download", sampleFileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };


  return (
    <>
      <Box
        onDrop={disableDragAndDrop ? undefined : (e) => {
          e.preventDefault();
          setIsDragging(false);
          processFile(e.dataTransfer.files);
        }}
        onDragOver={disableDragAndDrop ? undefined : (e) => 
        {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={disableDragAndDrop ? undefined : () => setIsDragging(false)} onClick={(e) => {
          if (onUploadClick) {
            if (onUploadClick() && !disabled) {
              inputRef.current.click();
            }
          }
        }}
        sx={{
          border: "2px dashed #bbbbbb",
          borderRadius: 2,
          p: 1,
          m: 0.5,
          textAlign: "center",
          cursor: disabled ? "not-allowed" : "pointer",
          bgcolor: isDragging ? "#e3f2fd" : "#f5f5f5",
          transition: "background-color 0.3s ease",
          "&:hover": {
            bgcolor: "#e3f2fd",
          },
        }}
      >
        <input
          ref={inputRef}
          type="file"
          hidden
          accept={normalizedAllowedExtensions.join(",")}
          onChange={(e) => processFile(e.target.files)}
        />

        <Stack direction="row" alignItems="center" justifyContent="space-between">
          {/* Left Section: Sample Template */}
          <Stack direction="row" alignItems="center" gap={1} flex={1}>
            <UploadFileOutlinedIcon sx={{ fontSize: 35 }} />
            <Typography
              sx={{
                cursor: "pointer",
                color: "primary.main",
                textDecoration: "underline",
                whiteSpace: "nowrap",
              }}
              onClick={handleDownload}
            >
              Sample Template
            </Typography>
            {extraLeftContent && (
              <Box onClick={(e) => e.stopPropagation()} sx={{ ml: 1 }}>
                {extraLeftContent}
              </Box>
            )}
          </Stack>


          {/* Middle Section */}
          <Stack direction="row" alignItems="center" justifyContent="center" gap={2} flex={1}>
            <UploadFileOutlinedIcon sx={{ fontSize: 35 }} />
            <Stack>
              <Typography fontSize={16} fontWeight={500} noWrap>
                {disableDragAndDrop ? "Upload files" : "Drag and drop or upload files"}
              </Typography>
              <Typography fontSize={12}>
                File formats allowed: {acceptedFormatsText}
              </Typography>
            </Stack>
          </Stack>
          <Typography fontSize={12} color="text.secondary" sx={{ visibility: email ? 'visible' : 'hidden' }}>
            OR
          </Typography>
          {/* Right Section */}
          <Stack
            direction="row"
            alignItems="center"
            gap={1}
            flex={1}
            justifyContent="center"
          >
            {email && (
              <Typography fontWeight={500}>
                {email}
              </Typography>
            )}
          </Stack>
        </Stack>
      </Box>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={hideSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        TransitionComponent={TransitionLeft}
      >
        <Alert onClose={hideSnackbar} severity={snackbar.severity} variant="filled" sx={sharedSnackbarAlertSx}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default FileUploadBox;
